import React, { useMemo } from "react"
import ReactApexChart from "react-apexcharts"
import moment from "moment"
import {
  getSameWeekPreviousYear,
  getWeekTooltipLabel,
  getSameWeekNextYear,
} from "utils/dateUtil"

const OEEChart = ({ data, previousData }) => {
  // ── Union of all ISO weeks from both years ──────────────────────────────
  const allWeeks = useMemo(() => {
    const weekSet = new Set()
    data?.forEach(i => weekSet.add(moment(i.date).isoWeek()))
    previousData?.forEach(i => weekSet.add(moment(i.date).isoWeek()))
    return Array.from(weekSet).sort((a, b) => a - b)
  }, [data, previousData])

  // ── Current year data ───────────────────────────────────────────────────
  const chartData = useMemo(() => {
    if (!allWeeks.length) return { categories: [], actualDates: {}, values: [] }

    const scoreMap = {}
    const dateMap = {}

    ;(data || []).forEach(i => {
      const w = moment(i.date).isoWeek()
      scoreMap[w] = Number(i.efficiency) || 0
      dateMap[w] = moment(i.date).format("DD-MM-YYYY")
    })

    return {
      categories: allWeeks.map(w => `${w}`),
      actualDates: dateMap,
      values: allWeeks.map(w =>
        scoreMap[w] !== undefined ? scoreMap[w] : null
      ),
    }
  }, [allWeeks, data])

  // ── Previous year data ──────────────────────────────────────────────────
  const prevChartData = useMemo(() => {
    if (!allWeeks.length) return { actualDates: {}, values: [] }

    const scoreMap = {}
    const dateMap = {}

    ;(previousData || []).forEach(i => {
      const w = moment(i.date).isoWeek()
      scoreMap[w] = Number(i.efficiency) || 0
      dateMap[w] = moment(i.date).format("DD-MM-YYYY")
    })

    return {
      actualDates: dateMap,
      values: allWeeks.map(w =>
        scoreMap[w] !== undefined ? scoreMap[w] : null
      ),
    }
  }, [allWeeks, previousData])

  // ── Year labels ─────────────────────────────────────────────────────────
  const currentYear = data?.length
    ? moment(data[0].date).year()
    : moment().year()

  const previousYear = previousData?.length
    ? moment(previousData[0].date).year()
    : currentYear - 1

  // ── Stats (ignore nulls) ────────────────────────────────────────────────
  const validValues = chartData.values.filter(v => v !== null)
  const avgEfficiency = validValues.length
    ? (validValues.reduce((a, b) => a + b, 0) / validValues.length).toFixed(1)
    : 0
  const maxVal = validValues.length ? Math.max(...validValues) : 0
  const minVal = validValues.length ? Math.min(...validValues) : 0

  // ── Chart options ───────────────────────────────────────────────────────
  const chartOptions = {
    chart: {
      type: "bar",
      height: 340,
      toolbar: { show: false },
      background: "transparent",
      foreColor: "#1a1a2e",
    },
    plotOptions: {
      bar: { borderRadius: 6, columnWidth: "55%" },
    },
    colors: ["#6366f1", "#22c55e", "#f59e0b"],
    dataLabels: { enabled: false },
    stroke: {
      show: true,
      width: [0, 2.5, 2.5],
      curve: "smooth",
      dashArray: [0, 0, 6],
      colors: ["transparent", "#22c55e", "#f59e0b"],
    },
    xaxis: {
      categories: chartData.categories,
      overwriteCategories: chartData.categories.map((val, index) => {
        return `${index + 1}`
      }),
      axisBorder: { show: false },
      labels: {
        rotate: -45,
        style: { colors: "#1a1a2e", fontSize: "12px", fontWeight: 600 },
      },
    },
    yaxis: {
      title: {
        text: "OEE (%)",
        style: { color: "#1a1a2e", fontSize: "12px", fontWeight: 700 },
      },
      min: 0,
      max: 120,
      labels: {
        formatter: val => `${val}%`,
        style: { colors: "#1a1a2e", fontSize: "12px", fontWeight: 600 },
      },
    },
    grid: {
      borderColor: "#e8e8f0",
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
    },
    tooltip: {
      shared: true,
      theme: "light",
      intersect: false,
      x: {
        formatter: (val, { dataPointIndex }) => {
          const weekNum = allWeeks[dataPointIndex]

          const currentDate = chartData.actualDates[weekNum]
          const prevDate = prevChartData.actualDates[weekNum]

          // Derive missing date using real ISO week logic
          const resolvedCurrentDate =
            currentDate ?? (prevDate ? getSameWeekNextYear(prevDate) : null)

          const resolvedPrevDate =
            prevDate ??
            (currentDate ? getSameWeekPreviousYear(currentDate) : null)

          console.log(resolvedCurrentDate, resolvedPrevDate)

          const currentLabel = getWeekTooltipLabel(
            dataPointIndex,
            resolvedCurrentDate
          )
          const prevLabel = getWeekTooltipLabel(
            dataPointIndex,
            resolvedPrevDate
          )

          return `Current Year : ${
            currentLabel || "N/A"
          }<br/> Previous Year : ${prevLabel || "N/A"}`
        },
      },
      y: {
        formatter: (val, { seriesIndex }) => {
          if (seriesIndex === 0) return undefined
          return val !== null && val !== undefined ? `${val}%` : "N/A"
        },
      },
    },
    legend: { show: false },
  }

  // ── Series ──────────────────────────────────────────────────────────────
  const chartSeries = [
    { name: "OEE Efficiency", type: "bar", data: chartData.values },
    {
      name: `Current Year (${currentYear})`,
      type: "line",
      data: chartData.values,
    },
    {
      name: `Previous Year (${previousYear})`,
      type: "line",
      data: prevChartData.values,
    },
  ]

  return (
    <div>
      {/* ── Header ── */}
      <div className="d-flex justify-content-between align-items-start mb-2 px-2">
        <h5
          className="mb-2 fw-bold"
          style={{ color: "#1a1a2e", fontSize: "16px" }}
        >
          OEE — Overall Equipment Effectiveness
        </h5>

        {/* ── Stats ── */}
        <div className="d-flex gap-4 px-2">
          <StatBox title="PEAK" value={maxVal} color="#22c55e" bg="#f0fdf4" />
          <StatBox
            title="AVERAGE"
            value={avgEfficiency}
            color="#6366f1"
            bg="#eef0fb"
          />
          <StatBox title="LOWEST" value={minVal} color="#ef4444" bg="#fef2f2" />
        </div>
      </div>

      {/* ── Chart ── */}
      <ReactApexChart
        options={chartOptions}
        series={chartSeries}
        type="bar"
        height={320}
        width="100%"
      />

      {/* ── Legend ── */}
      <div className="d-flex justify-content-center gap-4 mt-1 mb-2">
        <Legend color="#22c55e" label={`Current Year (${currentYear})`} />
        <Legend
          color="#f59e0b"
          label={`Previous Year (${previousYear})`}
          dashed
        />
      </div>
    </div>
  )
}

// ── Small reusable components ───────────────────────────────────────────────
const StatBox = ({ title, value, color, bg }) => (
  <div
    style={{
      flex: 1,
      background: bg,
      borderRadius: "10px",
      padding: "10px 18px",
      borderLeft: `4px solid ${color}`,
    }}
    data-tooltip={`${title} Value from All the Data`}
  >
    <div style={{ fontSize: "11px", fontWeight: 600 }}>{title}</div>
    <div style={{ fontSize: "18px", fontWeight: 800, color }}>{value}%</div>
  </div>
)

const Legend = ({ color, label, dashed }) => (
  <div className="d-flex align-items-center gap-1">
    <span
      style={{
        width: 16,
        height: 2,
        borderTop: dashed ? `2px dashed ${color}` : `2px solid ${color}`,
        display: "inline-block",
      }}
    />
    <small style={{ fontWeight: 600, fontSize: "11px" }}>{label}</small>
  </div>
)

export default OEEChart
